import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react';
import { GeoJsonProperties, Geometry, Feature } from 'geojson';
import { useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import GenericIcons from '../../components/generics/GenericIcons';
import Map from '../../components/maps/Map';
import { SimpleObject, StudyConfigType, StudyType } from '../../types';
import { getUncatchEndpointData } from '../../utils/request';
import MultiSlider from '../../components/MultiSlider';
import SelectInput, { SelectDataType } from '../../components/SelectInput';
import Swal from 'sweetalert2';
import { MyStudiesCRUD, OtherStudiesCRUD } from '../../components/GeneratedComponents';
import { AddStudyButton } from '../../components/SecondaryButton';
import CheckBox from '../../components/CheckBox';
import '../../css/PrivateStudiesPage.css';

const PrivateStudiesPage: FunctionComponent = () => {
  const [view, setView] = useState<string>('1');

  return view === '1' ? <Studies setView={setView} /> : <AddStudy setView={setView} />;
};

export default PrivateStudiesPage;

const STUDY_MENU_PAPER_SX = {
  mt: 1,
  minWidth: 260,
  borderRadius: 0,
  border: '1px solid var(--vdr-line)',
  borderTop: '3px solid var(--vdr-cyan)',
  boxShadow: 'var(--vdr-shadow-overlay)'
};

const STUDY_MENU_ITEM_SX = {
  minHeight: 44,
  px: 2.25,
  fontFamily: 'var(--vdr-font-display)',
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--vdr-text)',
  '&:hover': { backgroundColor: 'var(--vdr-soft)', color: 'var(--vdr-ink)' }
};

const Studies: FunctionComponent<{ setView: Dispatch<SetStateAction<string>> }> = ({ setView }) => {
  const navigate = useNavigate();
  const [studyType, setStudyType] = useState<'private' | 'public'>('private');
  const [isHoveredBtn, setIsHoveredBtn] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  return (
    <div className='private-studies-page-container'>
      <header className='vdr-page-head vdr-studies-head'>
        <div className='vdr-page-head__titles'>
          <span className='vdr-eyebrow vdr-eyebrow--bar'>Hosting capacity · Conexión GD</span>
          <h2 className='vdr-page-head__title'>Estudios</h2>
        </div>
        <div className='vdr-page-head__actions'>
          <button
            onMouseEnter={() => setIsHoveredBtn(true)}
            onMouseLeave={() => setIsHoveredBtn(false)}
            className='primary-button-style vdr-studies-add-btn'
            onClick={(e) => setAnchorEl(e.currentTarget)}
            style={{ height: 'clamp(42px, 3vw, 48px)' }}>
            <GenericIcons
              icon={isHoveredBtn ? 'plusCircleBlueIcon' : 'plusCircleWhiteIcon'}
              imageStyle={{ height: '100%', aspectRatio: '1/1' }}
            />
            Agregar Estudio
          </button>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: STUDY_MENU_PAPER_SX } }}>
            <MenuItem
              sx={STUDY_MENU_ITEM_SX}
              onClick={() => {
                setAnchorEl(null);
                setView('2');
              }}>
              Estudio Hosting Capacity
            </MenuItem>
            <MenuItem
              sx={STUDY_MENU_ITEM_SX}
              onClick={() => {
                setAnchorEl(null);
                navigate('/private/estudio-conexion/nuevo');
              }}>
              Estudio de Conexión GD
            </MenuItem>
          </Menu>
        </div>
      </header>
      <div className='private-studies-page-description-container'>
        <p className='private-studies-page-description-text'>
          Realiza tus propios Estudios y revisa otros Estudios que han sido compartidos por otros
          usuarios o empresas.
        </p>
      </div>
      <div className='private-studies-page-buttons-container'>
        <button
          className={`clear-button-style ${
            studyType === 'private' ? 'clear-button-style-checked' : ''
          }`}
          onClick={() => setStudyType('private')}>
          Mis estudios
        </button>
        <button
          className={`clear-button-style ${
            studyType === 'public' ? 'clear-button-style-checked' : ''
          }`}
          onClick={() => setStudyType('public')}>
          Otros estudios
        </button>
      </div>
      <div>
        {studyType === 'private' ? (
          <MyStudiesCRUD />
        ) : studyType === 'public' ? (
          <OtherStudiesCRUD />
        ) : null}
      </div>
    </div>
  );
};

const AddStudy: FunctionComponent<{ setView: Dispatch<SetStateAction<string>> }> = ({
  setView
}) => {
  const [typeStudy, setTypeStudy] = useState<StudyType>('public-study');
  const [loading, setLoading] = useState<boolean>(false);
  const [mapLoading, setMapLoading] = useState<boolean>(false);
  const [alimentadores, setAlimentadores] = useState<Array<SelectDataType>>([]);
  const [selectedAlimentador, setSelectedAlimentador] = useState<string>('');
  const [empresas, setEmpresas] = useState<Array<SelectDataType>>([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState<string>('');
  const [subEstaciones, setSubEstaciones] = useState<Array<SelectDataType>>([]);
  const [selectedSubEstacion, setSelectedSubEstacion] = useState<string>('');
  const [filteredAlimentadores, setFilteredAlimentadores] = useState<Array<SelectDataType>>([]);

  const getData = useCallback(
    async (
      endpoint: string,
      setState: Dispatch<SetStateAction<Array<SelectDataType>>>,
      valueKey: string,
      nameKey: string,
      query?: any
    ) => {
      try {
        setLoading(true);
        const res = await getUncatchEndpointData({ endpoint, query });
        if (res && res.length > 0) {
          setState(
            res.map((row: SimpleObject) => ({
              value: row[valueKey],
              name: row[nameKey]
            }))
          );
        }
        setLoading(false);
      } catch (error) {
        console.log(error);
        setState([]);
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    getData('alimentador', setAlimentadores, 'alimentadorId', 'alimentadorNombre');
    getData('empresa', setEmpresas, 'empresaId', 'empresaNombre');
  }, [getData]);

  const [state, setState] = useState({
    mapPointFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapLineFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapPolygonFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    electricFeeders: {} as SimpleObject,
    mapCenter: {} as {
      lat: number;
      lng: number;
    }
  });

  const [infoAlimentador, setInfoAlimentador] = useState<
    | {
        alimentadorId: string;
        alimentadorNombre: string;
        empresaId: string;
        empresaNombre: string;
        sseePoderId: string;
        sseePoderNombre: string;
      }
    | undefined
  >(undefined);

  const stateCleaner = () => {
    setSelectedEmpresa('');
    setSelectedSubEstacion('');
    setSelectedAlimentador('');
    setSubEstaciones([]);
    setFilteredAlimentadores([]);
    setInfoAlimentador(undefined);
  };

  const fetchMapPoints = useCallback(async (alimentadorId: string) => {
    if (!alimentadorId) return;
    try {
      setMapLoading(true);

      const allElementsMap = await getUncatchEndpointData({
        endpoint: 'mapElements/geoJSON/all',
        query: { alimentadorId }
      });
      const { pointFeatures, lineFeatures, polygonFeatures, alimentadores } = allElementsMap;

      if (
        (!pointFeatures || pointFeatures.length === 0) &&
        (!lineFeatures || lineFeatures.length === 0)
      ) {
        Swal.fire({
          icon: 'warning',
          title: 'No se encontraron lineas ni artefactos'
        }).finally(() => {
          stateCleaner();
        });
        return;
      }

      const localMapCenter = {} as {
        lat: number;
        lng: number;
      };

      if (pointFeatures && pointFeatures.length > 0 && lineFeatures && lineFeatures.length > 0) {
        localMapCenter['lng'] = pointFeatures[1].geometry.coordinates[0];
        localMapCenter['lat'] = pointFeatures[1].geometry.coordinates[1];
      } else if (lineFeatures && lineFeatures.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No se encontraron artefactos'
        }).finally(() => {
          stateCleaner();
        });
        localMapCenter['lng'] = lineFeatures[1].geometry.coordinates[0][0];
        localMapCenter['lat'] = lineFeatures[1].geometry.coordinates[0][1];
      } else if (pointFeatures && pointFeatures.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No se encontraron lineas'
        }).finally(() => {
          stateCleaner();
        });
        localMapCenter['lng'] = pointFeatures[1].geometry.coordinates[0];
        localMapCenter['lat'] = pointFeatures[1].geometry.coordinates[1];
      }

      setState((prevState) => ({
        ...prevState,
        mapPointFeatures: pointFeatures,
        mapLineFeatures: lineFeatures,
        mapPolygonFeatures: polygonFeatures,
        mapCenter: localMapCenter,
        electricFeeders: alimentadores
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setMapLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedAlimentador !== '') {
      fetchMapPoints(selectedAlimentador);
      const update = async () => {
        const res: Array<{
          alimentadorId: string;
          alimentadorNombre: string;
          empresaId: string;
          empresaNombre: string;
          sseePoderId: string;
          sseePoderNombre: string;
        }> = await getUncatchEndpointData({
          endpoint: 'alimentador/byAlimentadorId',
          query: { alimentadorId: selectedAlimentador }
        });
        if (res && res.length > 0) {
          setInfoAlimentador(res[0]);
        } else {
          setInfoAlimentador(undefined);
        }
      };
      update();
    } else {
      setInfoAlimentador(undefined);
      setState((prev) => ({
        ...prev,
        mapPointFeatures: [],
        mapLineFeatures: [],
        mapPolygonFeatures: [],
        electricFeeders: {},
        mapCenter: {} as { lat: number; lng: number }
      }));
    }
  }, [selectedAlimentador, fetchMapPoints]);

  useEffect(() => {
    if (infoAlimentador) {
      setSelectedEmpresa(infoAlimentador.empresaId);
      setSelectedSubEstacion(infoAlimentador.sseePoderId);
      setSelectedAlimentador(infoAlimentador.alimentadorId);
    }
  }, [infoAlimentador]);

  useEffect(() => {
    if (selectedEmpresa !== '') {
      getData('sseePoder', setSubEstaciones, 'sseePoderId', 'sseePoderNombre', {
        empresaId: selectedEmpresa
      });
      if (!infoAlimentador) {
        setSelectedSubEstacion('');
        setSelectedAlimentador('');
      }
    } else {
      setSubEstaciones([]);
    }
  }, [getData, selectedEmpresa]);

  useEffect(() => {
    if (selectedSubEstacion) {
      getData('alimentador', setFilteredAlimentadores, 'alimentadorId', 'alimentadorNombre', {
        sseePoderId: selectedSubEstacion
      });
      if (!infoAlimentador) {
        setSelectedAlimentador('');
      }
    } else {
      setSelectedAlimentador('');
      setFilteredAlimentadores([]);
    }
  }, [getData, selectedSubEstacion]);

  const [nombreSimulacion, setNombreSimulacion] = useState<string>('');
  const [descripcionSimulacion, setDescripcionSimulacion] = useState<string>('');

  const [config, setConfig] = useState<StudyConfigType>({
    pvResidencial: {
      checked: false,
      min: 0,
      max: 10
    },
    batResidencial: {
      checked: false,
      min: 0,
      max: 0
    },
    penetracionBateriasPct: 0,
    tapDinamico: false,
    reactivosPv: false,
    bateriasResidenciales: false,
    evResidencialCheck: false
  });

  const clearAllInputs = useCallback(() => {
    setNombreSimulacion('');
    setDescripcionSimulacion('');
    setTypeStudy('public-study');
    setSelectedEmpresa('');
    setSelectedSubEstacion('');
    setSelectedAlimentador('');
    setSubEstaciones([]);
    setFilteredAlimentadores([]);
    setConfig({
      pvResidencial: {
        checked: false,
        min: 0,
        max: 10
      },
      batResidencial: {
        checked: false,
        min: 0,
        max: 0
      },
      penetracionBateriasPct: 0,
      tapDinamico: false,
      reactivosPv: false,
      bateriasResidenciales: false,
      evResidencialCheck: false
    });
  }, []);

  return (
    <div className='private-studies-page-container'>
      <header className='private-add-study-title-container vdr-page-head'>
        <div className='vdr-page-head__titles'>
          <span className='vdr-eyebrow vdr-eyebrow--bar'>Hosting capacity · Nuevo estudio</span>
          <h2 className='vdr-page-head__title'>
            Agregar Estudio de Capacidad de Alojamiento de Alimentador
          </h2>
        </div>
        <div className='vdr-page-head__actions'>
          <button className='secondary-button-style vdr-studies-back-btn' onClick={() => setView('1')}>
            <span aria-hidden='true'>←</span>
            Volver
          </button>
        </div>
      </header>
      {/* General */}
      <div className='private-add-study-form-container'>
        <h3 className='private-add-study-form-title'>General</h3>
        <div className='private-add-study-form-general-container'>
          <div className='private-add-study-form-general-divided-container'>
            <div className='private-add-study-form-general-input-container private-add-study-form-general-input-container-30'>
              <label className='primary-label-style'>Nombre del Estudio</label>
              <input
                type='text'
                placeholder='???'
                className='private-add-study-form-text-input'
                value={nombreSimulacion}
                onChange={(e) => setNombreSimulacion(e.target.value)}
              />
            </div>
            <div className='private-add-study-form-general-input-container private-add-study-form-general-input-container-70'>
              <label className='primary-label-style'>Descripción del Estudio</label>
              <input
                type='text'
                placeholder='???'
                className='private-add-study-form-text-input'
                value={descripcionSimulacion}
                onChange={(e) => setDescripcionSimulacion(e.target.value)}
              />
            </div>
          </div>
          <div className='private-add-study-form-general-radio-form-container'>
            <div className='private-add-study-form-general-radio-input-container'>
              <input
                type='radio'
                className='primary-radio-input-style'
                value='public-study'
                checked={typeStudy === 'public-study'}
                onChange={(e) => setTypeStudy(e.target.value as StudyType)}
              />
              <label
                className='primary-label-style private-add-study-form-label-cursor-pointer'
                onClick={() => setTypeStudy('public-study')}>
                Estudio Público
              </label>
            </div>
            <div className='private-add-study-form-general-radio-input-container'>
              <input
                type='radio'
                className='primary-radio-input-style'
                value='private-study'
                checked={typeStudy === 'private-study'}
                onChange={(e) => setTypeStudy(e.target.value as StudyType)}
              />
              <label
                className='primary-label-style private-add-study-form-label-cursor-pointer'
                onClick={() => setTypeStudy('private-study')}>
                Estudio Privado
              </label>
            </div>
          </div>
        </div>
      </div>
      {/* Alimentador */}
      <div className='private-add-study-form-container'>
        <h3 className='private-add-study-form-title'>Alimentador</h3>
        <div className='private-add-study-feeder-form-container'>
          <div className='private-add-study-form-feeder-container-30'>
            <div className='private-add-study-form-feeder-search-container'>
              <h3 className='private-add-study-form-feeder-subtitle'>Búsqueda por nombre</h3>
              <SelectInput
                icon='searchIcon'
                label='Nombre de alimentador'
                name='name-feeder'
                id='name-feeder'
                value={selectedAlimentador}
                onChange={(e) => setSelectedAlimentador(e.target.value)}
                options={alimentadores}
              />
            </div>
            <div className='private-add-study-form-feeder-divider-line'></div>
            <div className='private-add-study-form-feeder-search-container'>
              <h3 className='private-add-study-form-feeder-subtitle'>Búsqueda por Empresa</h3>
              <div className='private-add-study-form-feeder-company-search-container'>
                <div className='private-add-study-form-feeder-select-container'>
                  <label className='primary-label-style'>Empresa</label>
                  <SelectInput
                    label='Selecciona Empresa'
                    name='company'
                    id='company'
                    value={selectedEmpresa}
                    onChange={(e) => {
                      setInfoAlimentador(undefined);
                      setSelectedEmpresa(e.target.value);
                    }}
                    options={empresas}
                  />
                </div>
                <div className='private-add-study-form-feeder-select-container'>
                  <label className='primary-label-style'>Subestación</label>
                  <SelectInput
                    label='Selecciona Subestación'
                    name='substation'
                    id='substation'
                    value={selectedSubEstacion}
                    onChange={(e) => {
                      setInfoAlimentador(undefined);
                      setSelectedSubEstacion(e.target.value);
                    }}
                    options={subEstaciones}
                  />
                </div>
                <div className='private-add-study-form-feeder-select-container'>
                  <label className='primary-label-style'>Alimentador</label>
                  <SelectInput
                    label='Selecciona Alimentador'
                    name='feeder'
                    id='feeder'
                    value={selectedAlimentador}
                    onChange={(e) => setSelectedAlimentador(e.target.value)}
                    options={filteredAlimentadores}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='private-add-study-form-feeder-container-70'>
            <Map
              mapPointFeatures={state.mapPointFeatures}
              mapLineFeatures={state.mapLineFeatures}
              mapPolygonFeatures={state.mapPolygonFeatures}
              electricFeeders={state.electricFeeders}
              mapCenter={state.mapCenter}
            />
            {mapLoading && (
              <div className='private-add-study-map-loader-container'>
                <span className='loader'></span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Configuración del Estudio */}
      <div className='private-add-study-form-container'>
        <h3 className='private-add-study-form-title'>Configuración del Estudio</h3>
        <div className='private-add-study-form-config-container'>
          <div className='private-add-study-form-config-section-container'>
            <h3 className='private-add-study-form-config-subtitle'>Habilitar DERs</h3>
          </div>
          <div className='private-add-study-form-config-section-container'>
            <h3 className='private-add-study-form-config-subtitle'>Rango de</h3>
          </div>
          <div className='private-add-study-form-config-section-container'>
            <h3 className='private-add-study-form-config-subtitle'>Avanzadas</h3>
          </div>
        </div>
        <div className='private-add-study-form-config-container'>
          <div className='private-add-study-form-config-section-container'>
            <CheckBox
              label='PV residencial'
              onChange={(e) =>
                setConfig({
                  ...config,
                  pvResidencial: { ...config.pvResidencial, checked: e.target.checked }
                })
              }
              checked={config.pvResidencial.checked}
            />
          </div>
          <div className='private-add-study-form-config-section-container'>
            <MultiSlider
              value={[config.pvResidencial.min, config.pvResidencial.max]}
              onChange={(_: Event, newValue: number | number[]) =>
                setConfig({
                  ...config,
                  pvResidencial: {
                    ...config.pvResidencial,
                    min: (newValue as number[])[0],
                    max: (newValue as number[])[1]
                  }
                })
              }
            />
          </div>
          <div className='private-add-study-form-config-section-container'>
            <GenericIcons
              icon='gearIcon'
              imageStyle={{ marginLeft: '2.5vw', height: '1.2vw', width: '1.2vw' }}
            />
          </div>
        </div>
        <div className='private-add-study-form-config-container'>
          <div className='private-add-study-form-config-section-container'>
            <CheckBox
              label='Bat. residencial'
              onChange={(e) => {
                const checked = e.target.checked;
                const pct = checked
                  ? config.penetracionBateriasPct > 0
                    ? config.penetracionBateriasPct
                    : 10
                  : 0;
                setConfig({
                  ...config,
                  batResidencial: { ...config.batResidencial, checked, min: pct, max: pct },
                  penetracionBateriasPct: pct
                });
              }}
              checked={config.batResidencial.checked}
            />
          </div>
          <div className='private-add-study-form-config-section-container'>
            <MultiSlider
              singleThumb
              disabled={!config.batResidencial.checked}
              value={config.penetracionBateriasPct}
              onChange={(_: Event, newValue: number | number[]) => {
                const pct = typeof newValue === 'number' ? newValue : (newValue as number[])[0];
                setConfig({
                  ...config,
                  penetracionBateriasPct: pct,
                  batResidencial: { ...config.batResidencial, min: pct, max: pct }
                });
              }}
            />
          </div>
          <div className='private-add-study-form-config-section-container'>
            <GenericIcons
              icon='gearIcon'
              imageStyle={{ marginLeft: '2.5vw', height: '1.2vw', width: '1.2vw' }}
            />
          </div>
        </div>
        <div className='private-add-study-form-config-container'>
          <div className='private-add-study-form-config-section-container'>
            <h3 className='private-add-study-form-config-subtitle'>
              Habilitar Medida de Mitigación
            </h3>
          </div>
          <div className='private-add-study-form-config-section-container'></div>
          <div className='private-add-study-form-config-section-container'>
            <h3 className='private-add-study-form-config-subtitle'>Avanzadas</h3>
          </div>
        </div>
        <div className='private-add-study-form-config-container'>
          <div className='private-add-study-form-config-section-container'>
            <CheckBox
              label='Tap dinámico'
              onChange={(e) =>
                setConfig({
                  ...config,
                  tapDinamico: e.target.checked
                })
              }
              checked={config.tapDinamico}
            />
          </div>
          <div className='private-add-study-form-config-section-container'></div>
          <div className='private-add-study-form-config-section-container'>
            <GenericIcons
              icon='gearIcon'
              imageStyle={{ marginLeft: '2.5vw', height: '1.2vw', width: '1.2vw' }}
            />
          </div>
        </div>
        <div className='private-add-study-form-config-container'>
          <div className='private-add-study-form-config-section-container'>
            <CheckBox
              label='Reactivos PV'
              onChange={(e) =>
                setConfig({
                  ...config,
                  reactivosPv: e.target.checked
                })
              }
              checked={config.reactivosPv}
            />
          </div>
          <div className='private-add-study-form-config-section-container'></div>
          <div className='private-add-study-form-config-section-container'>
            <GenericIcons
              icon='gearIcon'
              imageStyle={{ marginLeft: '2.5vw', height: '1.2vw', width: '1.2vw' }}
            />
          </div>
        </div>
        <div className='private-add-study-form-config-container'>
          <div className='private-add-study-form-config-section-container'>
            <CheckBox
              label='Baterías residenciales'
              onChange={(e) =>
                setConfig({
                  ...config,
                  bateriasResidenciales: e.target.checked
                })
              }
              checked={config.bateriasResidenciales}
            />
          </div>
          <div className='private-add-study-form-config-section-container'></div>
          <div className='private-add-study-form-config-section-container'>
            <GenericIcons
              icon='gearIcon'
              imageStyle={{ marginLeft: '2.5vw', height: '1.2vw', width: '1.2vw' }}
            />
          </div>
        </div>
        <div className='private-add-study-form-config-container'>
          <div className='private-add-study-form-config-section-container'>
            <CheckBox
              label='EV residencial'
              onChange={(e) =>
                setConfig({
                  ...config,
                  evResidencialCheck: e.target.checked
                })
              }
              checked={config.evResidencialCheck}
            />
          </div>
          <div className='private-add-study-form-config-section-container'></div>
          <div className='private-add-study-form-config-section-container'>
            <GenericIcons
              icon='gearIcon'
              imageStyle={{ marginLeft: '2.5vw', height: '1.2vw', width: '1.2vw' }}
            />
          </div>
        </div>
      </div>
      <div className='private-add-study-action-buttons-container'>
        <AddStudyButton
          nombreSimulacion={nombreSimulacion}
          descripcionSimulacion={descripcionSimulacion}
          typeStudy={typeStudy}
          alimentadorId={selectedAlimentador}
          config={config}
          clearForm={() => clearAllInputs()}
          goToMyStudies={() => setView('1')}
        />
      </div>
    </div>
  );
};

// =============================================================================
// TODO Fase 5 — Estudio de Conexión GD: Mejoras a esta página
// =============================================================================
// Esta página se reutilizará como lista unificada de todos los tipos de estudio
// del usuario (Hosting Capacity + Conexión GD). No se crea una página de lista
// separada para Conexión GD (decisión UX: ver PLAN_ESTUDIO_CONEXION_GD.md §3.3.1).
//
// Cambios necesarios en esta página:
//
// 1. DOS COLUMNAS DIFERENCIADAS en la tabla:
//    a) "Visibilidad" (antes era "Tipo"): "Pública" | "Privada"
//       - HC: se obtiene de tipo_simulacion_id → tipo_simulacion (catálogo existente).
//       - Conexión GD: siempre "Privada".
//    b) "Tipo de estudio" (nueva): "Hosting Capacity" | "Conexión GD PMGD" | "Conexión GD EG"
//
// 2. FILTRO POR TIPO: dropdown o tabs ("Todos" | "Hosting Capacity" | "Conexión GD")
//    encima de la tabla para que el usuario pueda filtrar.
//
// 3. COLUMNAS CONDICIONALES según tipo de estudio:
//    - Para Conexión GD: mostrar "Tipo GD", "Potencia (kW)", "Estado base",
//      "Estado mitigación".
//    - Para HC: mostrar las columnas actuales.
//    - Columnas no aplicables al tipo se muestran como "—".
//
// 4. BOTÓN "Agregar Estudio": debe ofrecer selector de tipo:
//    - "Estudio Hosting Capacity" → flujo actual (setView('2'))
//    - "Estudio de Conexión GD" → redirigir a /private/estudio-conexion/nuevo
//
// 5. ENRUTAMIENTO POR TIPO al hacer clic en una fila:
//    - HC → vista de detalle actual
//    - Conexión GD → /private/estudio-conexion/:id/resultado
//
// Enfoque de BD (decidido):
//
// - Se usará una VISTA SQL `v_estudios_usuario` que hace UNION ALL de la tabla
//   `simulacion` (HC) y `estudio_conexion_gd` (Conexión GD), exponiendo columnas
//   comunes: estudio_id, tabla_origen, tipo_estudio, visibilidad, user_account_id,
//   nombre, alimentador_id, alimentador_nombre, estado, estado_mitigacion,
//   created_at, tipo_gd, potencia_kw, archivo_resultado.
// - El campo `tabla_origen` ('simulacion' | 'estudio_conexion_gd') permite al
//   frontend decidir la ruta de detalle al hacer clic en una fila.
// - El campo `visibilidad` ('Publica' | 'Privada') se obtiene de tipo_simulacion
//   para HC y es siempre 'Privada' para Conexión GD.
// - El endpoint GET /api/estudios/myStudies consultará esta vista.
// - Ver detalle completo en FASE03_TABLAS_BD.md sección 2.
// =============================================================================
